import React from 'react';
import PropTypes from 'prop-types';
import { Grid, GridItem, Flex, VStack, Text, Button } from '@chakra-ui/react';

import Image from '@repo/ui-components/tailwind/Image';
import Link from '@repo/ui-components/tailwind/Link';
import QuantityCounter from '@repo/ui-components/tailwind/QuantityCounter';
import { pushToDataLayer } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { AttributesShape, VariantShape } from './PropShapes';
import CartItemDetails from './CartItemDetails';

function CartItem({
  id,
  variant,
  title,
  quantity,
  deleteLineItem,
  incrementLineItem,
  decrementLineItem,
  attributes,
  sellingPlanAllocation,
}) {
  const { tt, localizePrice } = useUiComponentsInternationalContext();
  let totalPrice = variant.price.amount * quantity || 0;

  // Product is a subscription, that has discounted price
  if (sellingPlanAllocation?.priceAdjustments?.[0]?.price?.amount) {
    totalPrice =
      sellingPlanAllocation.priceAdjustments[0].price.amount * quantity;
  }

  const formattedPrice = localizePrice(totalPrice);

  const handleQuantityChange = newQuantity => {
    if (newQuantity > quantity) {
      incrementLineItem(id, newQuantity);
    } else if (newQuantity < quantity) {
      decrementLineItem(id, newQuantity);
    }
  };

  const removeItem = () => {
    deleteLineItem(id);
    pushToDataLayer('remove_from_cart', {
      ecommerce: {
        currency: 'USD',
        value: totalPrice.toFixed(2),
      },
      items: [
        {
          variant,
        },
      ],
    });
  };

  return (
    <VStack
      w="full"
      py={6}
      gap={6}
      borderBottom="1px solid"
      borderColor="stroke.light"
    >
      {/* Cart Item Details */}
      <Grid
        as={Link}
        link={`/product/${variant?.product?.handle}`}
        w="full"
        gap={4}
        templateColumns="repeat(6, 1fr)"
      >
        <GridItem colSpan="2">
          {variant?.image?.src && (
            <Image
              alt={`${tt(s => s.cartDeptComponents.thumbnailAlt)} - ${title}`}
              src={`${variant.image.src}&width=128`}
              width="100%"
            />
          )}
        </GridItem>

        <GridItem colSpan="4">
          <Flex
            flexDir="column"
            alignItems="flex-start"
            textAlign="left"
            textStyle="caption"
            gap={2}
            flex={1}
          >
            <Text fontWeight="bold">{title}</Text>
            <CartItemDetails attributes={attributes} variant={variant} />
          </Flex>
        </GridItem>
      </Grid>

      {/* Cart Actions */}

      <Flex w="full" gap={4} justify="space-between" align="center">
        <Button variant="link" onClick={removeItem}>
          {tt(s => s.cartDeptComponents.removeButton)}
        </Button>

        <QuantityCounter
          disableManualEntry
          max={variant?.quantityAvailable}
          quantity={quantity}
          setQuantity={handleQuantityChange}
        />

        <Text fontWeight="bold">{formattedPrice}</Text>
      </Flex>
    </VStack>
  );
}

CartItem.propTypes = {
  id: PropTypes.string.isRequired,
  variant: VariantShape.isRequired,
  attributes: AttributesShape.isRequired,
  title: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  deleteLineItem: PropTypes.func.isRequired,
  incrementLineItem: PropTypes.func.isRequired,
  decrementLineItem: PropTypes.func.isRequired,
  sellingPlanAllocation: PropTypes.shape({
    priceAdjustments: PropTypes.shape({
      price: PropTypes.shape({
        amount: PropTypes.number,
      }),
    }),
  }),
};

CartItem.defaultProps = {
  sellingPlanAllocation: null,
};

export default CartItem;
